/**
 * Builders for the 3 real backend list-query conventions
 * (`standards/ApiStandards.md`, `FRONTEND-BACKEND-INTEGRATION.md` §5)
 * — never hand-built ad hoc per call site, so a convention change only
 * needs updating here.
 *
 * - Pagination: `?page=&limit=` (`shared/pagination`'s `PaginationQuery`).
 * - Sorting: `?sort=field:asc` / `field:desc` — one field, one
 *   direction, validated against a per-endpoint allowlist server-side
 *   (`shared/query/sort.builder.ts`).
 * - Filtering: `?<field>=<operator>:<value>` — e.g. `status=eq:ACTIVE`,
 *   matching `MasterRepository.search()`'s own real filter-string
 *   convention (the shape a Stage 5C.3 defect was found and fixed
 *   against, `FINANCIAL-APPLICATION-ARCHITECTURE.md`).
 */
export interface PaginationParams {
  page: number;
  limit: number;
}

export type SortDirection = 'asc' | 'desc';

export interface SortParams {
  field: string;
  direction: SortDirection;
}

export type FilterOperator = 'eq' | 'ne' | 'gt' | 'gte' | 'lt' | 'lte' | 'like' | 'in';

export interface FilterParam {
  field: string;
  operator: FilterOperator;
  value: string | number | boolean;
}

export function buildListQueryParams(options: {
  pagination?: PaginationParams;
  sort?: SortParams;
  filters?: FilterParam[];
}): Record<string, string> {
  const params: Record<string, string> = {};

  if (options.pagination) {
    params.page = String(options.pagination.page);
    params.limit = String(options.pagination.limit);
  }

  if (options.sort) {
    params.sort = `${options.sort.field}:${options.sort.direction}`;
  }

  for (const filter of options.filters ?? []) {
    params[filter.field] = `${filter.operator}:${String(filter.value)}`;
  }

  return params;
}

export const DEFAULT_PAGE_SIZE = 25;
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;
